import { useState } from 'react';

import { buttonClass } from '@league/ui';
import { GoogleMark } from '../../components/GoogleMark';
import { AuthCard, Button, ErrorNote, Field, Input } from '../../components/ui';
import {
  GOOGLE_SIGN_IN_URL,
  requestSetPasswordLink,
  sendSignInCode,
  type SignInOptions,
  signInWithPassword,
  verifySignInCode,
} from '../../lib/auth';
import { isNetworkError, unlessOffline } from '../../lib/api';

type Step = 'password' | 'code' | 'forgot' | 'link-sent';

/**
 * Signing in: an address, a password, and — for anyone who holds other people's data — a six-digit
 * code emailed to that same address.
 *
 * No authenticator app and no QR code. That was built here once and rejected: it puts an enrolment
 * wall in the doorway, and `otpauth://` is not a web link, so pointing a phone camera at the code
 * does nothing at all. See DECISIONS.md.
 *
 * Whether the second step happens is the SERVER'S answer, read from `twoFactorRedirect` — not a
 * guess from the role. A copy of that rule here would be a copy that can disagree with it.
 */
export function LoginPage({
  brand,
  options,
  onSignedIn,
}: {
  brand: string;
  options: SignInOptions;
  onSignedIn: () => void;
}) {
  /*
    ARRIVING FROM "SET UP YOUR ACCOUNT" in a league email (2026-10-05): open at "Set your password"
    with her address filled in. It sends nothing until she presses the button — mail scanners open
    every link in an email, and an automatic send would fire for them, not for her.
  */
  const [setupFor] = useState(() => new URLSearchParams(window.location.search).get('set-password'));
  const [step, setStep] = useState<Step>(setupFor !== null ? 'forgot' : 'password');
  const [email, setEmail] = useState(setupFor ?? '');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [linkExpiry, setLinkExpiry] = useState(options.linkExpiryMinutes);

  function fail(message: string) {
    setError(message);
  }

  async function submitPassword(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      const result = await signInWithPassword(email.trim(), password);

      if (!result.twoFactorRedirect) {
        onSignedIn();
        return;
      }

      /**
       * The code is requested for them rather than behind another button. They have already proved
       * the password; making them ask for the thing they obviously want next is friction with no
       * security value.
       */
      await sendSignInCode();
      setPassword('');
      setCode('');
      setStep('code');
    } catch (error) {
      /** One message for a wrong address and a wrong password — naming which is a free hint. */
      fail(unlessOffline(error, 'That email and password do not match. Check both and try again.'));
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      await verifySignInCode(code.trim());
      onSignedIn();
    } catch (error) {
      fail(unlessOffline(error, 'That code is not right, or it has expired. Ask for a new one.'));
      /* Her code stays when it never reached us; a wrong one is cleared for the next try. */
      if (!isNetworkError(error)) setCode('');
    } finally {
      setBusy(false);
    }
  }

  async function resendCode() {
    setError(null);
    setBusy(true);
    try {
      await sendSignInCode();
      setCode('');
    } catch (error) {
      fail(unlessOffline(error, 'We could not send another code just now. Try again in a moment.'));
    } finally {
      setBusy(false);
    }
  }

  async function submitForgot(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      const { expiresInMinutes } = await requestSetPasswordLink(email.trim());
      setLinkExpiry(expiresInMinutes);
      setStep('link-sent');
    } catch (error) {
      fail(unlessOffline(error, 'We could not send that link just now. Try again in a moment.'));
    } finally {
      setBusy(false);
    }
  }

  if (step === 'code') {
    return (
      <AuthCard
        brand={brand}
        title="Check your email"
        intro={
          <>
            We sent a six-digit code to <span className="text-ink">{email.trim()}</span>. It expires
            in {options.codeExpiryMinutes} minutes.
          </>
        }
      >
        <form onSubmit={submitCode} className="flex flex-col gap-5">
          <Field label="Code from your email" htmlFor="code">
            <Input
              id="code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoComplete="one-time-code"
              inputMode="numeric"
              autoFocus
              required
              className="tabular text-center text-lg tracking-[0.3em]"
              placeholder="000000"
            />
          </Field>

          {error ? <ErrorNote>{error}</ErrorNote> : null}

          <Button type="submit" disabled={busy || code.trim().length === 0}>
            {busy ? 'Checking…' : 'Sign in'}
          </Button>

          <div className="flex justify-center gap-5 text-[13px]">
            <Button
              type="button"
              variant="secondary"
              size="inline"
              disabled={busy}
              onClick={() => void resendCode()}
            >
              Send another code
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="inline"
              onClick={() => {
                setStep('password');
                setError(null);
              }}
            >
              Start over
            </Button>
          </div>
        </form>
      </AuthCard>
    );
  }

  if (step === 'link-sent') {
    return (
      <AuthCard
        brand={brand}
        title="Check your email"
        intro={
          <>
            If <span className="text-ink">{email.trim()}</span> is on file, there is a link on its
            way. It works once and expires in {linkExpiry} minutes.
          </>
        }
      >
        <Button
          type="button"
          variant="secondary"
          className="w-full"
          onClick={() => {
            setStep('password');
            setError(null);
          }}
        >
          Back to sign in
        </Button>
      </AuthCard>
    );
  }

  if (step === 'forgot') {
    return (
      <AuthCard
        brand={brand}
        title="Set your password"
        intro="New here, or lost your password? Both are the same link. We'll email it to you."
      >
        <form onSubmit={submitForgot} className="flex flex-col gap-5">
          <Field label="Email" htmlFor="forgot-email">
            <Input
              id="forgot-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              autoFocus
              required
            />
          </Field>

          {error ? <ErrorNote>{error}</ErrorNote> : null}

          <Button type="submit" disabled={busy}>
            {busy ? 'Sending…' : 'Email me a link'}
          </Button>

          <div className="flex justify-center text-[13px]">
            <Button
              type="button"
              variant="secondary"
              size="inline"
              onClick={() => {
                setStep('password');
                setError(null);
              }}
            >
              Back to sign in
            </Button>
          </div>
        </form>
      </AuthCard>
    );
  }

  return (
    <AuthCard brand={brand} title="Sign in">
      <div className="flex flex-col gap-5">
        {options.google ? (
          <>
            <a href={GOOGLE_SIGN_IN_URL} className={buttonClass('secondary', 'app')}>
              <GoogleMark />
              Continue with Google
            </a>

            <div className="flex items-center gap-3 text-[12px] text-ink-3">
              <span className="h-px flex-1 bg-light" />
              or
              <span className="h-px flex-1 bg-light" />
            </div>
          </>
        ) : null}

        <form onSubmit={submitPassword} className="flex flex-col gap-5">
          <Field label="Email" htmlFor="email">
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              autoFocus={!options.google}
              required
            />
          </Field>

          <Field label="Password" htmlFor="password">
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </Field>

          {error ? <ErrorNote>{error}</ErrorNote> : null}

          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>

          <div className="flex justify-center text-[13px]">
            <Button
              type="button"
              variant="secondary"
              size="inline"
              onClick={() => {
                setStep('forgot');
                setError(null);
              }}
            >
              Set or reset your password
            </Button>
          </div>
        </form>
      </div>
    </AuthCard>
  );
}
